import { FormDialog } from '@/components/FormDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { PasswordInput } from '@/components/PasswordInput'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect, type MultiSelectGroup, type MultiSelectOption } from '@/components/ui/multi-select'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { isApiError } from '@/utils/error'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateUser, useGetUserById, useUpdateUser, useUpdateUserDepartments } from '../hooks/useUser'
import { CreateUserModel, UserRole } from '../types/user'

type UserForm = {
  username: string
  password: string
  name: string
  avatarId: string
  roleId: string
  departmentIds: string[]
  primaryDepartmentId: string
}

const emptyForm: UserForm = {
  username: '',
  password: '',
  name: '',
  avatarId: '',
  roleId: '',
  departmentIds: [],
  primaryDepartmentId: '',
}

function areStringArraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false

  const sortedA = [...a].sort()
  const sortedB = [...b].sort()

  return sortedA.every((value, index) => value === sortedB[index])
}

export function UserDialog({
  open,
  onOpenChange,
  userId,
  mode,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  userId?: string | null
  mode: 'create' | 'edit'
}) {
  const { t } = useTranslation()
  const { userRoles } = useUI()
  const { data: configs } = useGetConfigs()

  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const updateUserDepartments = useUpdateUserDepartments()

  const {
    data: user,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
    refetch: refetchUser,
  } = useGetUserById(mode === 'edit' ? userId : null)
  const {
    data: departments = [],
    isLoading: isLoadingDepartments,
    isFetching: isFetchingDepartments,
  } = useGetDepartmentOptions(undefined, open)

  const [form, setForm] = useState<UserForm>(emptyForm)
  const [initialForm, setInitialForm] = useState<UserForm>(emptyForm)
  const shouldSelectDepartment = form.roleId !== UserRole.Vendor && form.roleId !== UserRole.Maintainer

  const selectedDepartments = departments.filter(department => form.departmentIds.includes(department.id))
  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]

  const isUserChanged =
    mode === 'create' ||
    form.name !== initialForm.name ||
    form.avatarId !== initialForm.avatarId ||
    form.roleId !== initialForm.roleId

  const isDepartmentChanged =
    shouldSelectDepartment &&
    (mode === 'create' ||
      form.primaryDepartmentId !== initialForm.primaryDepartmentId ||
      !areStringArraysEqual(form.departmentIds, initialForm.departmentIds))

  const hasChanges = mode === 'create' || isUserChanged || isDepartmentChanged

  const avatarField = useFileUploadField({
    value: form.avatarId ? [form.avatarId] : [],
    onChange: ids => setForm(current => ({ ...current, avatarId: ids[0] || '' })),
    maxFiles: 1,
    onUploadError: error => {
      if (isApiError(error)) showError(error.message)
    },
  })

  const departmentOptions = useMemo<MultiSelectOption[]>(
    () =>
      departments.map(department => {
        const description = formatBuildingFloorLocation(t, department.buildingName, department.floorName)

        return {
          value: department.id,
          label: department.name,
          description,
          searchValue: `${department.name} ${department.buildingName ?? ''} ${department.floorName ?? ''}`,
        }
      }),
    [departments, t],
  )
  const departmentGroups = useMemo<MultiSelectGroup[]>(
    () => [
      {
        label: t('scans.mainWarehouse'),
        options: departmentOptions.filter(department => department.value === warehouseDepartmentId),
      },
      {
        label: t('scans.otherDepartments'),
        options: departmentOptions.filter(department => department.value !== warehouseDepartmentId),
      },
    ],
    [departmentOptions, t, warehouseDepartmentId],
  )
  const selectedDepartmentGroups = useMemo<SearchableSelectGroup[]>(
    () => [
      {
        label: t('scans.mainWarehouse'),
        options: selectedDepartments
          .filter(department => department.id === warehouseDepartmentId)
          .map(department => ({
            value: department.id,
            label: department.name,
            description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
            searchValue: `${department.name} ${department.buildingName ?? ''} ${department.floorName ?? ''}`,
          })),
      },
      {
        label: t('scans.otherDepartments'),
        options: selectedDepartments
          .filter(department => department.id !== warehouseDepartmentId)
          .map(department => ({
            value: department.id,
            label: department.name,
            description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
            searchValue: `${department.name} ${department.buildingName ?? ''} ${department.floorName ?? ''}`,
          })),
      },
    ],
    [selectedDepartments, t, warehouseDepartmentId],
  )

  useEffect(() => {
    if (open && mode === 'edit' && userId) refetchUser()
  }, [open, mode, userId, refetchUser])

  function handleDepartmentIdsChange(departmentIds: string[]) {
    setForm(current => {
      return {
        ...current,
        departmentIds,
        primaryDepartmentId: departmentIds.includes(current.primaryDepartmentId) ? current.primaryDepartmentId : '',
      }
    })
  }

  function handlePrimaryDepartmentChange(departmentId: string) {
    setForm(current => ({
      ...current,
      primaryDepartmentId: departmentId,
      departmentIds: Array.from(new Set([...current.departmentIds, departmentId])),
    }))
  }

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && user) {
      const departmentIds = user.departments?.map(department => department.id) ?? []
      const nextForm = {
        username: user.username ?? '',
        password: '',
        name: user.name,
        avatarId: user.avatarId ?? '',
        roleId: user.role,
        departmentIds,
        primaryDepartmentId: user.primaryDepartment?.id ?? '',
      }

      setForm(nextForm)
      setInitialForm(nextForm)
    } else if (mode === 'create') {
      setForm(emptyForm)
      setInitialForm(emptyForm)
    }
  }, [user, open, mode])

  async function handleSubmit() {
    if (!form.username || !form.name || !form.roleId) return
    if (mode === 'create' && !form.password) return
    if (mode === 'edit' && !hasChanges) return

    try {
      let savedUserId = userId ?? null

      if (mode === 'edit' && userId) {
        if (isUserChanged) {
          await updateUser.mutateAsync({
            id: userId,
            payload: {
              name: form.name,
              avatarId: form.avatarId || null,
              role: form.roleId,
            },
          })
        }
      } else {
        const payload: CreateUserModel = {
          username: form.username,
          password: form.password,
          name: form.name,
          avatarId: form.avatarId || undefined,
          role: form.roleId,
          primaryDepartmentId: shouldSelectDepartment ? form.primaryDepartmentId || undefined : undefined,
        }
        const createdUser = await createUser.mutateAsync(payload)
        savedUserId = createdUser.id
      }

      if (savedUserId && (isDepartmentChanged || (!shouldSelectDepartment && initialForm.departmentIds.length > 0))) {
        await updateUserDepartments.mutateAsync({
          id: savedUserId,
          payload: {
            departmentIds: shouldSelectDepartment ? form.departmentIds : [],
            primaryDepartmentId: shouldSelectDepartment ? form.primaryDepartmentId || null : null,
          },
        })
      }

      onOpenChange(false)
      showSuccess(mode === 'edit' ? t('users.updateSuccess') : t('users.createSuccess'))
    } catch (error: unknown) {
      if (isApiError(error)) showError(error.message)
    }
  }

  const isLoading = createUser.isPending || updateUser.isPending || updateUserDepartments.isPending

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'edit' ? t('users.editUser') : t('users.addUser')}
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingUser || isFetchingUser}
      isInvalid={
        isLoading ||
        isLoadingUser ||
        isFetchingUser ||
        avatarField.isUploading ||
        (shouldSelectDepartment && (isLoadingDepartments || isFetchingDepartments)) ||
        !hasChanges ||
        !form.name ||
        !form.username ||
        !form.roleId ||
        (mode === 'create' && !form.password)
      }
      contentTestId="user-dialog"
      submitTestId="user-form-submit"
    >
      <div className="grid grid-cols-1 gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.username')} *</Label>
          <Input
            data-testid="user-form-username"
            value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
            disabled={mode === 'edit'}
            autoComplete="off"
            name="new-user-username"
          />
        </div>

        {mode === 'create' && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('common.password')} *</Label>
            <PasswordInput
              data-testid="user-form-password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="h-9 bg-secondary/50 text-xs"
              placeholder=""
              autoComplete="new-password"
              name="new-user-password"
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.name')} *</Label>
          <Input
            data-testid="user-form-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.role')} *</Label>
          <Select
            value={form.roleId}
            onValueChange={roleId =>
              setForm(current => ({
                ...current,
                roleId,
                ...(roleId === UserRole.Vendor || roleId === UserRole.Maintainer
                  ? { departmentIds: [], primaryDepartmentId: '' }
                  : {}),
              }))
            }
          >
            <SelectTrigger data-testid="user-form-role-trigger" className="h-9 bg-secondary/50 text-xs w-full">
              <SelectValue placeholder="" />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {userRoles?.map(role => (
                <SelectItem data-testid={`user-form-role-option-${role.value}`} key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.avatar')}</Label>
          <ImageUpload
            files={avatarField.files}
            imageIds={avatarField.imageIds}
            onChange={avatarField.handleChange}
            onRemoveExisting={avatarField.handleRemoveExisting}
            maxImages={1}
          />
          {avatarField.isUploading ? (
            <div className="text-[11px] text-muted-foreground">
              {t('users.uploadingAvatar')}
              {avatarField.uploadProgress ? ` ${avatarField.uploadProgress}%` : '...'}
            </div>
          ) : null}
        </div>

        {shouldSelectDepartment && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t('users.responsibleDepartments')}</Label>
              {isLoadingDepartments || isFetchingDepartments ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <MultiSelect
                  testId="user-form-departments"
                  groups={departmentGroups}
                  value={form.departmentIds}
                  onValueChange={handleDepartmentIdsChange}
                  placeholder={t('common.placeholder.selectDepartments')}
                  searchPlaceholder={t('common.placeholder.searchDepartment')}
                  emptyText={t('common.noDepartment')}
                />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t('common.primaryDepartment')}</Label>
              <SearchableSelect
                testId="user-form-primary-department"
                value={form.primaryDepartmentId}
                onValueChange={handlePrimaryDepartmentChange}
                groups={selectedDepartmentGroups}
                placeholder={selectedDepartments.length === 0 ? t('common.placeholder.selectDepartmentFirst') : ''}
                searchPlaceholder={t('common.placeholder.searchDepartment')}
                emptyText={t('common.noDepartment')}
                disabled={selectedDepartments.length === 0}
                triggerClassName="bg-secondary/50 text-xs"
              />
            </div>
          </>
        )}
      </div>
    </FormDialog>
  )
}
